import { useDrawerStore } from "@/stores/useDrawerStore";
import { useTableHeadStore } from "@/stores/useTableHeadStore";
import {
  useCompaniesTableStore,
  useVehiclesTableStore
} from "@/stores/useTableStore";
import { useTutorialStore } from "@/stores/useTutorialStore";

import { ROUTES, useMediaQueries } from "../../../shared";
import { STEP_GROUP } from "../types";
import useTutorialStepGroups from "../useTutorialStepGroups";

const useTutorialMode = () => {
  const { fromMd, toSm } = useMediaQueries();
  const {
    companySelectorStepsDesktop,
    companySelectorStepsMobile,
    addNewCompanyCardStepsDesktop,
    addNewCompanyCardStepsMobile,
    addVehicleStep,
    applyScheduleStepsDesktop,
    applyScheduleStepsMobile,
    createScheduleStepsDesktop,
    createScheduleStepsMobile
  } = useTutorialStepGroups(fromMd, toSm);

  const resetCompaniesTableDetails = useCompaniesTableStore(
    state => state.resetTableDetails
  );

  const resetVehiclesTableDetails = useVehiclesTableStore(
    state => state.resetTableDetails
  );

  const setSelectedVehicleRows = useVehiclesTableStore(
    state => state.setSelectedRows
  );

  const isSettingsOpen = useTutorialStore(state => state.isSettingsOpen);
  const tutorialDetails = useTutorialStore(state => state.tutorialDetails);
  const setTutorialDetails = useTutorialStore(
    state => state.setTutorialDetails
  );
  const setIsSettingsOpen = useTutorialStore(state => state.setIsSettingsOpen);
  const setTutorialCompanySelectorClose = useTutorialStore(
    state => state.setCompanySelectorTutorialStatus
  );

  const setIsMobileDrawerOpen = useDrawerStore(
    state => state.setIsMobileDrawerOpen
  );

  const setIsMobileTableHeadActive = useTableHeadStore(
    state => state.setTableHeadActive
  );

  const { isTutorialOpen, stepIndex, stepGroup, isTutorialActive } =
    tutorialDetails;

  const callTutorialWithTimeout = (
    isOpen: boolean,
    index: number,
    group: STEP_GROUP,
    isActive: boolean,
    interval: number
  ) => {
    setTimeout(() => {
      setTutorialDetails({
        isTutorialOpen: isOpen,
        stepIndex: index,
        stepGroup: group,
        isTutorialActive: isActive
      });
    }, interval);
  };

  const handleClearVehiclesTableHead = () => {
    setSelectedVehicleRows([]);
    setIsMobileTableHeadActive(false);
  };

  const clearTableHeadIfApplySchedule = () => {
    stepGroup === STEP_GROUP.TutorialApplySchedule &&
      handleClearVehiclesTableHead();
  };

  const checkIfCorrectTutorialStep = (
    inputStepGroup: STEP_GROUP,
    index: number
  ) => isTutorialActive && inputStepGroup === stepGroup && index === stepIndex;

  const handleTutorialExit = (closeLocation?: ROUTES) => {
    const companySelectorSecondStep =
      stepIndex === 1 && stepGroup === STEP_GROUP.TutorialCompanySelector;

    setTutorialDetails({
      isTutorialOpen: false,
      stepIndex: 0,
      stepGroup: STEP_GROUP.Default,
      isTutorialActive: false
    });

    if (isTutorialActive) {
      setIsSettingsOpen(false);
      setIsMobileDrawerOpen(false);
      clearTableHeadIfApplySchedule();
      companySelectorSecondStep && setTutorialCompanySelectorClose(true); // this state trigers useEffect in CompanySelector.tsx
    }
  };

  const handleTutorialInCompaniesTable = (closeDetails: () => void) => {
    if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialNewCompanyCard, 2)) {
      closeDetails();

      !isSettingsOpen &&
        setTutorialDetails({
          isTutorialOpen: true,
          stepIndex,
          stepGroup,
          isTutorialActive: true
        });
    } else {
      handleTutorialExit(ROUTES.Companies);
      isTutorialActive && resetCompaniesTableDetails();
    }
  };

  const handleTutorialInVehiclesTable = (numberOfVehicles: number) => {
    const tutorialSteps: { [key: string]: number } = {
      [STEP_GROUP.TutorialCreateSchedule]: fromMd ? 2 : 3,
      [STEP_GROUP.TutorialApplySchedule]: fromMd ? 1 : 2
    };

    if (
      stepGroup === STEP_GROUP.TutorialApplySchedule &&
      numberOfVehicles === 0
    ) {
      return setTutorialDetails({
        isTutorialOpen: true,
        stepIndex: 0,
        stepGroup: STEP_GROUP.TutorialAddVehicle,
        isTutorialActive: true
      });
    } else if (
      checkIfCorrectTutorialStep(stepGroup, tutorialSteps[stepGroup])
    ) {
      resetVehiclesTableDetails();
      setSelectedVehicleRows([]);

      return setTutorialDetails({
        isTutorialOpen: true,
        stepIndex: tutorialSteps[stepGroup],
        stepGroup,
        isTutorialActive: true
      });
    }

    return handleTutorialExit(ROUTES.Vehicles);
  };

  const handleTutorialStepIndexChange = (nextStepIndex: number) => {
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: nextStepIndex,
      stepGroup: stepGroup,
      isTutorialActive: true
    });
  };

  const currentStepGroup = (() => {
    switch (stepGroup) {
      case STEP_GROUP.TutorialCompanySelector:
        return fromMd
          ? companySelectorStepsDesktop
          : companySelectorStepsMobile;

      case STEP_GROUP.TutorialNewCompanyCard:
        return fromMd
          ? addNewCompanyCardStepsDesktop
          : addNewCompanyCardStepsMobile;

      case STEP_GROUP.TutorialCreateSchedule:
        return fromMd ? createScheduleStepsDesktop : createScheduleStepsMobile;

      case STEP_GROUP.TutorialApplySchedule:
        return fromMd ? applyScheduleStepsDesktop : applyScheduleStepsMobile;

      case STEP_GROUP.TutorialAddVehicle:
        return addVehicleStep;

      default:
        return [];
    }
  })();

  return {
    isSettingsOpen,
    isTutorialOpen,
    stepIndex,
    stepGroup,
    isTutorialActive,
    currentStepGroup,
    callTutorialWithTimeout,
    checkIfCorrectTutorialStep,
    clearTableHeadIfApplySchedule,
    handleClearVehiclesTableHead,
    handleTutorialInCompaniesTable,
    handleTutorialInVehiclesTable,
    handleTutorialStepIndexChange,
    handleTutorialExit,
    setTutorialDetails,
    setIsSettingsOpen,
    setIsMobileDrawerOpen
  };
};

export default useTutorialMode;
